import { useEffect, useMemo, useState } from "react";

import type { IssueCredential } from "../../types";

import {
  type RemoteIssueProject,
  fetchRemoteIssueProjects,
} from "../../api/issueIntegrations";

export type RemoteProjectsStatus = "idle" | "loading" | "ready" | "error";

export interface RemoteProjectsState {
  status: RemoteProjectsStatus;
  projects: RemoteIssueProject[];
  error: string | null;
  reload: () => void;
  hasCredentials: boolean;
}

export function useRemoteProjects(
  providerId: string,
  credentials: IssueCredential[],
): RemoteProjectsState {
  const [status, setStatus] = useState<RemoteProjectsStatus>("idle");
  const [projects, setProjects] = useState<RemoteIssueProject[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const signature = useMemo(
    () =>
      credentials
        .map(
          (credential) =>
            `${credential.credential_id}:${credential.updated_at}:${credential.last_refreshed_at ?? 0}:${credential.access_token_expires_at ?? 0}`,
        )
        .toSorted()
        .join("|"),
    [credentials],
  );

  useEffect(() => {
    if (!credentials.length) {
      setStatus("idle");
      setProjects([]);
      setError(null);
      return;
    }

    let active = true;
    setStatus("loading");
    setError(null);

    fetchRemoteIssueProjects(providerId)
      .then((fetched) => {
        if (!active) {
          return;
        }
        const sorted = [...fetched].toSorted((left, right) => {
          const a = (left.name ?? left.external_key).toLowerCase();
          const b = (right.name ?? right.external_key).toLowerCase();
          return a.localeCompare(b);
        });
        setProjects(sorted);
        setStatus("ready");
      })
      .catch((loadError: unknown) => {
        if (!active) {
          return;
        }
        const message =
          loadError instanceof Error ? loadError.message : String(loadError);
        setProjects([]);
        setStatus("error");
        setError(message);
      });

    return () => {
      active = false;
    };
  }, [providerId, signature, credentials.length, reloadToken]);

  const reload = () => {
    if (!credentials.length) {
      return;
    }
    setStatus("loading");
    setError(null);
    setReloadToken((value) => value + 1);
  };

  return {
    error,
    hasCredentials: credentials.length > 0,
    projects,
    reload,
    status,
  };
}
